import React from 'react';

/* A deliberately small markdown reader, sized to the articles in /public/articles
   rather than to the whole spec. It supports exactly what those files use:

     # h1        the title, lifted out of the body
     ## h2       ### h3
     ---         rule; the first closes the title block, the last opens the callout
     *line*      a whole line in italics — used for the dek and the closing callout
     **bold**    *italic*  inline
     [text]      a bare bracket, used only for the Quick Look call to action
     - item      an unordered list; consecutive lines gather into one block
     1. item     an ordered list, same rule
     | a | b |   a table, with the --- separator row under the header
     > line      a quotation; consecutive lines gather the same way a list does
     `code`      a code span, exempt from the typographic quote pass
     [t](/path)  a link, same-site paths only

   Lists and tables were added for the two document pages. The reason is worth
   recording, because it is not "markdown has them so we should": the three
   billing arrangements were three sections of prose all built on the same
   sentence pattern, which is both harder to compare than a table and the exact
   thing that makes a page read as generated. A table is the honest shape for
   three options against four questions, and it happens to be the shape a
   language model can lift an answer out of.

   Quotations were added for the Deep Look page, which shows the reader a piece
   of the methodology it sells. That passage has to read as lifted from another
   document rather than as the page talking about itself, because the whole
   point of showing it is that somebody else wrote it for a different audience.
   Prose cannot do that on its own.

   Code spans came with it, for the same document. A report that quotes source
   has to render that source as source, and it also has to be kept out of the
   typographic quote pass, which would otherwise turn an array subscript into
   something that no longer parses in the language it was lifted from.

   Still no images, no fenced code blocks, no nested lists. Add those the same
   way if a page ever genuinely needs them. Do not swap in a markdown library
   and inherit its typography — the article page controls its own measure, rhythm and type,
   and a generic renderer would quietly undo that. */

/* Straight quotes into typographic ones.

   Everything hand-written in a view uses proper apostrophes, because they were
   typed that way. Everything that arrives as markdown does not — the article
   files and the two doc pages are written in a plain text editor, and one piece
   was carrying forty-one straight apostrophes against three curly ones. The two
   kinds of page sit a scroll apart, and a straight apostrophe beside Fraunces
   is the detail that reads as unfinished on exactly the pages whose job is to
   look like care was taken.

   Done here rather than in the source files so that writing a piece stays a
   matter of typing it. Done here rather than server-side for the same reason
   the front matter is stripped here: the file is the source of truth and should
   arrive whole.

   The order below is the whole trick. Doubles resolve first, so a contraction
   inside a quoted sentence is still an ordinary contraction when the singles
   pass reaches it. Then an apostrophe with a letter or a digit to its left is
   always a contraction or a possessive, whatever else is going on around it.
   Only what survives both is an opening quote.

   The emphasis markers count as opening context, and have to. The vetting
   piece asks its questions as `**"What would you build first?"**`, and a rule
   that only opened after whitespace read every one of those as a closing quote
   — twelve of them in that file alone, all pointing the wrong way, in the
   article most likely to be read by somebody deciding whether to trust us.

   Deliberately not touching dashes or ellipses. No article uses `--` or `...`,
   and a rule that rewrote them would have to know that `---` is a rule. */
const OPENS = '\\s(\\[{*_>—–';

const curl = text => text
    .replace(new RegExp(`(^|[${OPENS}])"`, 'g'), '$1“')
    .replace(/"/g, '”')
    // an elided leading digit — the '90s — before the opening rule can claim it
    .replace(/(^|\s)'(?=\d)/g, '$1’')
    .replace(/([A-Za-z0-9])'/g, '$1’')
    .replace(new RegExp(`(^|[${OPENS}“])'`, 'g'), '$1‘')
    .replace(/'/g, '’');

/* Code spans are exempt, and the exemption has to live here rather than in the
   renderer. typeset runs on the whole line in parseArticle, long before inline
   splits it, so by the time anything knows a span was code its quotes have
   already been curled. The sample report is the file that proved it: the
   published output of the tool quotes real source, and `$payload['user_id']`
   arriving as `$payload[‘user_id’]` is no longer the code it claims to be.

   Splitting on the span keeps the exemption exact. Prose either side of a code
   span still gets its proper apostrophes, which is the whole point of typeset
   and is not something a line containing code should have to give up. */
export const typeset = text => String(text)
    .split(/(`[^`]+`)/)
    .map(part => part.startsWith('`') ? part : curl(part))
    .join('');

/* Code first in the alternation, so a span containing an asterisk is read as
   code rather than broken open as emphasis. Source quoted in a report is full
   of characters that mean something to markdown and nothing to the language
   they came from.

   A real link, [text](/path), has to be tried before the bare bracket, or the
   bracket alternative claims the label and leaves the target sitting in the
   prose as literal parentheses. */
const INLINE = /(`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\])/g;

/* Links are same-site paths only, and that restriction is the feature.
   Anything written into these files is rendered as-is, so allowing an arbitrary
   href would mean a markdown file could point at any host on the internet, and
   these files are the part of the site most likely to be edited quickly. A
   leading slash and no protocol is the whole rule. */
const LINK = /^\[([^\]]+)\]\((\/[^)\s]*)\)$/;

// Splits a line into text and emphasis. Bracketed spans are returned as plain
// text; whoever renders them decides whether they become a link.
export const inline = text => {
    /* Coerced rather than trusted. This is called from four render paths, and a
       block type that carries `items` or `rows` instead of `text` reaching one
       of them used to throw inside a render and take the whole view blank — no
       error on screen, just an empty page. A shared primitive should degrade. */
    return String(text == null ? '' : text).split(INLINE).filter(part => part !== '').map((part, index) => {
        if(part.startsWith('`') && part.endsWith('`')) {
            return <code className={'md-code'} key={index}>{part.slice(1, -1)}</code>;
        }
        /* A plain anchor rather than a routed click. These are same-site paths
           and the app reads the address bar on load, so the navigation is
           correct either way; what a real href buys is the things a handler
           does not, which are a middle click, a copied link and a status bar
           that says where the reader is about to go. */
        const link = LINK.exec(part);
        if(link) {
            return <a className={'md-link'} href={link[2]} key={index}>{link[1]}</a>;
        }
        if(part.startsWith('**') && part.endsWith('**')) {
            return <b key={index}>{part.slice(2, -2)}</b>;
        }
        if(part.startsWith('*') && part.endsWith('*')) {
            return <i key={index}>{part.slice(1, -1)}</i>;
        }
        if(part.startsWith('[') && part.endsWith(']')) {
            return <React.Fragment key={index}>{part.slice(1, -1)}</React.Fragment>;
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
    });
}

/* Lists and tables, rendered once for both pages that show them.

   Article.js and DocPage.js each own their headings, because a doc page pulls a
   step number out of an h2 and an article does not. Nothing distinguishes a list
   or a table between them, so those live here rather than in two copies that
   would drift — which is exactly what happened to the nav before it was reduced
   to one list, and to the footer before that.

   Returns null for anything it does not handle, so a caller can treat it as
   "render this if it is one of mine" and fall through to its own cases. */
export const richBlock = (block, key) => {

    if(block.type === 'ul' || block.type === 'ol') {
        const List = block.type === 'ul' ? 'ul' : 'ol';
        return (
            <List className={'md-list'} key={key}>
                {block.items.map((item, index) => <li key={index}>{inline(item)}</li>)}
            </List>
        )
    }

    /* One <p> per gathered line rather than one joined paragraph. A quotation
       long enough to break is a quotation with paragraphs in it, and running
       them together would be the renderer editing the source. */
    if(block.type === 'quote') {
        return (
            <blockquote className={'md-quote'} key={key}>
                {block.lines.map((line, index) => <p key={index}>{inline(line)}</p>)}
            </blockquote>
        )
    }

    if(block.type === 'table') {
        return (
            /* The scroller, not the table, carries the overflow. A table set to
               scroll itself cannot, because the width it would scroll within is
               the width its own content demands. Three columns of prose is
               already wider than a phone. */
            <div className={'md-table-scroll'} key={key}>
                <table className={'md-table'} role={'table'}>
                    <thead>
                        <tr role={'row'}>{block.head.map((cell, index) => <th key={index} role={'columnheader'} scope={'col'}>{inline(cell)}</th>)}</tr>
                    </thead>
                    <tbody>
                        {block.rows.map((row, index) => (
                            <tr key={index} role={'row'}>
                                {row.map((cell, cellIndex) => (
                                    /* The first cell names the row, so it is a
                                       header for that row the same way the top
                                       cells are headers for their columns. A
                                       screen reader reading across row four
                                       announces "Subscription" with each value
                                       rather than four values and no subject. */
                                    cellIndex === 0
                                        ? <th key={cellIndex} role={'rowheader'} scope={'row'}>{inline(cell)}</th>
                                        /* data-label carries the column heading
                                           down to the cell so the stacked layout
                                           on a phone can print it. Without it
                                           "The scope can be pinned down" appears
                                           under a step name with nothing saying
                                           it answers "best when".

                                           The roles are explicit because
                                           display: block strips a table of its
                                           implicit ones, and the phone layout
                                           does exactly that. Stating them keeps
                                           the header association a screen reader
                                           relies on. */
                                        : <td data-label={block.head[cellIndex] || undefined} key={cellIndex} role={'cell'}>{inline(cell)}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    return null;
}

/* Articles carry a front matter block since they moved to the server: the fields
   the index is built from, fenced off at the top of the file. It is metadata for
   the server, not prose, and has to come off before anything below reads a line.

   Removed here rather than server-side because the file is the source of truth
   and should arrive whole. A reader that only understands the body is the piece
   that knows what a body is.

   Only a block opening on the very first line counts. The articles use a bare
   `---` as a rule further down, and the closing callout sits under the last one.
   Treating any of those as a fence would swallow the article. */
const stripFrontMatter = source => {

    if(source.indexOf('---\n') !== 0) {
        return source;
    }

    const end = source.indexOf('\n---', 3);
    return end === -1 ? source : source.slice(end + 4);
}

/* Returns { title, dek, blocks, callout }.

   `callout` is the italic paragraph after the final rule, which every article
   ends with to point at the Quick Look. It is handed back separately so the page
   can set it as a real call to action instead of one more paragraph of prose. */
export const parseArticle = source => {

    const lines = stripFrontMatter(String(source || '').replace(/\r\n/g, '\n')).split('\n');

    let title = null;
    let dek = null;
    const blocks = [];
    let callout = null;

    /* Lists and tables are the only multi-line blocks here, so rather than a
       second pass they accumulate as the lines go by and get closed the moment
       a line arrives that does not continue them. `close` is called from the
       top of the loop and again at the end, which is the whole of the state
       machine. */
    let open = null;

    const close = () => {
        if(!open) {
            return;
        }
        // a table whose header never got a body is a malformed table, not an
        // empty one, and rendering an empty shell hides the mistake. A quote
        // that gathered no text is the same mistake wearing different marks.
        const empty = (open.type === 'table' && open.rows.length === 0)
            || (open.type === 'quote' && open.lines.length === 0);

        if(!empty) {
            blocks.push(open);
        }
        open = null;
    }

    // "| a | b |" → ['a', 'b']. The outer pipes are optional the way they are
    // everywhere else, so both fenced and unfenced rows read the same.
    const cells = line => line.replace(/^\||\|$/g, '').split('|').map(cell => cell.trim());

    lines.forEach(raw => {

        /* Typeset before anything reads the line, so every branch below and
           every consumer downstream gets the same text: the title, the dek, the
           headings the rail is built from, the prose, and the callout. Nothing
           it rewrites is markdown syntax, so the tests for `---`, `# `, `## `
           and a fully italic line all still see what they expect. */
        const line = typeset(raw.trim());

        /* A blank line ends a list or a table, so two lists separated by one
           stay two lists. A rule ends one too, and neither is otherwise of
           interest here. */
        if(line === '' || line === '---') {
            close();
            return;
        }

        /* Tables. The separator row under the header (| --- | --- |) carries no
           content and is only there to say "the row above was a header", so it
           is recognised and dropped rather than rendered as a row of dashes. */
        if(line.startsWith('|')) {
            if(/^\|[\s|:-]+\|?$/.test(line)) {
                return;
            }
            if(!open || open.type !== 'table') {
                close();
                // the first row of a table is its header, always. Nothing here
                // needs a headerless table and guessing at one is how you get a
                // heading rendered as data.
                open = { type: 'table', head: cells(line), rows: [] };
                return;
            }
            open.rows.push(cells(line));
            return;
        }

        /* Quotations. The marker comes off each line, so the block holds the
           quoted text and nothing about how it was written. A bare ">" with no
           text after it is the blank line between two quoted paragraphs, and it
           has to keep the block open rather than close it the way a real blank
           line does. */
        if(line === '>' || line.startsWith('> ')) {
            if(!open || open.type !== 'quote') {
                close();
                open = { type: 'quote', lines: [] };
            }
            const text = line.slice(1).trim();
            if(text !== '') {
                open.lines.push(text);
            }
            return;
        }

        /* Lists. Ordered and unordered are separate types rather than one with a
           flag, because the two say different things — a sequence that has to
           happen in order against a set that does not — and the renderer picks
           ol or ul from that. */
        const ordered = /^\d+\.\s+/.exec(line);
        const bullet = /^[-*]\s+/.exec(line);
        if(ordered || bullet) {
            const type = ordered ? 'ol' : 'ul';
            const text = line.slice((ordered || bullet)[0].length).trim();
            if(!open || open.type !== type) {
                close();
                open = { type: type, items: [] };
            }
            open.items.push(text);
            return;
        }

        // anything else ends whatever was gathering
        close();

        if(line.startsWith('# ')) {
            title = line.slice(2).trim();
            return;
        }
        if(line.startsWith('### ')) {
            blocks.push({ type: 'h3', text: line.slice(4).trim() });
            return;
        }
        if(line.startsWith('## ')) {
            blocks.push({ type: 'h2', text: line.slice(3).trim() });
            return;
        }

        // a fully italic line before any prose is the dek; the same shape after
        // the article body is the closing callout
        const italic = line.startsWith('*') && line.endsWith('*') && !line.startsWith('**');
        if(italic && blocks.length === 0 && dek === null) {
            dek = line.slice(1, -1).trim();
            return;
        }
        /* The closing callout is the only italic line carrying a bracketed span,
           so a mid-article aside in italics still renders as ordinary prose. The
           bracket itself is dropped: it is the article's own way of writing "and
           here is the link", and the page renders that as a real button. */
        if(italic && line.indexOf('[') !== -1) {
            callout = line.slice(1, -1).replace(/\[[^\]]*\]/g, '').replace(/\s+/g, ' ').trim();
            return;
        }

        blocks.push({ type: 'p', text: line });
    });

    // a file ending on a list or a table closes it rather than dropping it
    close();

    return { title, dek, blocks, callout };
}
